'use client';
import { getQueryTimeoutMs } from '#pplx/frontend/api-clients/core/utils.js';
import { boundSHA256HexDigest } from '#pplx/frontend/common/safeCrypto.js';
import { getSingularDeviceIdCookieDomain } from '#pplx/frontend/growth/analytics-singular/cookie-domain/singularDeviceIdCookieDomain.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { attributionClient } from '#pplx_generated/typespec/rest/attribution/client.js';
import { createContext, useContext } from 'react';
export async function sha256Hex(input) {
    return boundSHA256HexDigest(input);
}
export async function _generateMatchId(email) {
    const hashedEmail = await sha256Hex(email);
    return `match_${hashedEmail}`;
}
export const startCometSession = async ({ reason, hasConsent, }) => {
    // Callers pass `hasConsent` from useGdprConsent, which already accounts for GPC.
    if (!hasConsent) {
        return { success: true };
    }
    try {
        const { data, error } = await attributionClient.POST(`/rest/attribution/comet/session`, reason, {
            timeoutMs: getQueryTimeoutMs(),
            numRetries: 1,
        });
        if (error) {
            throw error;
        }
        return {
            success: data.success,
        };
    }
    catch (err) {
        logger.error('Failed to start Comet session', err);
        return {
            success: false,
        };
    }
};
export const sendCometEvent = async ({ eventName, reason, hasConsent, }) => {
    // Callers pass `hasConsent` from useGdprConsent, which already accounts for GPC.
    if (!hasConsent) {
        return { success: true };
    }
    try {
        const { data, error } = await attributionClient.POST('/rest/attribution/comet/event/{event_name}', reason, {
            timeoutMs: getQueryTimeoutMs(),
            params: {
                path: {
                    event_name: eventName,
                },
            },
        });
        if (error) {
            throw error;
        }
        return {
            success: data?.success ?? false,
        };
    }
    catch (err) {
        logger.error('Failed to send Comet event', err);
        return {
            success: false,
        };
    }
};
export const SingularAttributionContext = createContext({
    sendQueryEventSingular: () => Promise.resolve(),
    maybeSendNthQueryEventSingular: () => Promise.resolve(),
    sendSubscribedEventSingular: () => Promise.resolve(),
    sendAppDownloadClickedEventSingular: () => Promise.resolve(),
    sendResurrectedActivationEventSingular: () => Promise.resolve(),
    sendApiOrgCreatedEventSingular: () => Promise.resolve(),
    sendApiCreditsAddedEventSingular: () => Promise.resolve(),
    sendApiKeyCreatedEventSingular: () => Promise.resolve(),
    sendApiPaymentMethodAddedEventSingular: () => Promise.resolve(),
    apiAttributionConsent: null,
    resolveApiAttribution: () => Promise.resolve(null),
    beginApiOrgAttributionRevalidation: () => { },
    setApiOrgEmailHash: () => Promise.resolve(),
    sendCometDownloadPageLoadEventSingular: () => Promise.resolve(),
    sendCometDownloadPageSignInEventSingular: () => Promise.resolve(),
    sendCometDownloadedEventSingular: () => Promise.resolve(),
    sendCometDownloadButtonClickedEventSingular: () => Promise.resolve(),
    sendCometWaitlistButtonClickedEventSingular: () => Promise.resolve(),
    sendCometDownloadLinkEmailEventSingular: () => Promise.resolve(),
    sendSubscribedMaxEventSingular: () => Promise.resolve(),
    sendSubscribedPayingEnterpriseEventSingular: () => Promise.resolve(),
    sendAssistantConnectorClickedEventSingular: () => Promise.resolve(),
    sendAssistantPaywallPageVisitEventSingular: () => Promise.resolve(),
    sendAssistantStartFreeTrialClickedEventSingular: () => Promise.resolve(),
    sendAssistantUpgradeToMaxClickedEventSingular: () => Promise.resolve(),
    sendAssistantUpgradeToProClickedEventSingular: () => Promise.resolve(),
    sendAssistantPaywallDismissedEventSingular: () => Promise.resolve(),
    sendAssistantConnectPageVisitEventSingular: () => Promise.resolve(),
    sendAssistantGreetingPageVisitEventSingular: () => Promise.resolve(),
    sendAssistantCalendarSelectionPageVisitEventSingular: () => Promise.resolve(),
    sendAssistantTimeSettingsPageVisitEventSingular: () => Promise.resolve(),
    sendAssistantAutoLabelPageVisitEventSingular: () => Promise.resolve(),
    sendAssistantAutoDraftPageVisitEventSingular: () => Promise.resolve(),
    sendAssistantCompletePageVisitEventSingular: () => Promise.resolve(),
    sendStudyHubPageVisitedEventSingular: () => Promise.resolve(),
    sendChatWithTutorClickedEventSingular: () => Promise.resolve(),
    sendPracticeQuestionsClickedEventSingular: () => Promise.resolve(),
    sendStudyGuideClickedEventSingular: () => Promise.resolve(),
    sendFlashcardsClickedEventSingular: () => Promise.resolve(),
    sendOnePagerClickedEventSingular: () => Promise.resolve(),
    sendSpacedRepetitionClickedEventSingular: () => Promise.resolve(),
    sendMaterialsUploadedEventSingular: () => Promise.resolve(),
    sendStudyHubActivatedEventSingular: () => Promise.resolve(),
    sendThirdStudyHubEngagementEventSingular: () => Promise.resolve(),
    sendFifthStudyHubEngagementEventSingular: () => Promise.resolve(),
    sendTenthStudyHubEngagementEventSingular: () => Promise.resolve(),
    sendTryComputerClickedEventSingular: () => Promise.resolve(),
    sendCheckoutInitiatedEventSingular: () => Promise.resolve(),
    sendActivatedComputerEventSingular: () => Promise.resolve(),
    sendConnectorActivatedEventSingular: () => Promise.resolve(),
    sendSlackAcquisitionLandingPageViewedEventSingular: () => Promise.resolve(),
    sendSlackAcquisitionCtaSelectedEventSingular: () => Promise.resolve(),
    sendSlackInstallClickEventSingular: () => Promise.resolve(false),
    sendUserConnectedPortfolioEventSingular: () => Promise.resolve(),
    sendMarketingEmailCollectedEventSingular: () => Promise.resolve(),
    sendMarketingFormCompletedEventSingular: () => Promise.resolve(),
    sendMarketingLargeOrgFormCompletedEventSingular: () => Promise.resolve(),
    sendMarketingMeetingBookedEventSingular: () => Promise.resolve(),
    sendSmbEmailCollectedEventSingular: () => Promise.resolve(),
    sendBuildersEmailCollectedEventSingular: () => Promise.resolve(),
    sendComputerEmailCollectedEventSingular: () => Promise.resolve(),
    sendStudentLandingPageViewedEventSingular: () => Promise.resolve(),
    sendStudentEmailCollectedEventSingular: () => Promise.resolve(),
    sendDemoEmailEnteredEventSingular: () => Promise.resolve(),
    sendDemoCtaClickedEventSingular: () => Promise.resolve(),
    sendApiConsoleCtaClickedEventSingular: () => Promise.resolve(),
    sendGatedAssetEventSingular: () => Promise.resolve(),
    setMatchIdWithEmail: () => Promise.resolve(),
});
SingularAttributionContext.displayName = 'SingularAttributionContext';
export const NTH_QUERY_TO_EVENT_NAME = {
    1: 'activated',
    3: 'true activated',
    5: 'quality activated',
    10: 'super activated',
};
export const NTH_COMET_QUERY_TO_EVENT_NAME = {
    1: 'first comet query',
    3: 'third comet query',
};
export const TEST_PRODUCT_ID = 'ai.testing.perplexity';
export const PROD_PRODUCT_ID = 'ai.perplexity';
export const getEventId = async (visitorId, eventName) => {
    if (!visitorId)
        return null;
    const dateToSecondsAccuracy = new Date();
    dateToSecondsAccuracy.setMilliseconds(0);
    const eventIdUnhashed = `${visitorId}-${dateToSecondsAccuracy.toISOString()}-${eventName}`;
    const eventId = await sha256Hex(eventIdUnhashed);
    return eventId;
};
export const SINGULAR_API_KEY = 'perplexity_ai_039eb8fb';
export const SINGULAR_API_TOKEN = '1e0179ca95965b57a3e91c1cf3049b64';
export const TRACKED_SINGULAR_CONNECTOR_ACTIVATED_KEY_PREFIX = 'pplx.singular.connectorActivated';
export const initSingular = async ({ productId, visitorId, userId, }) => {
    // dynamically import because it is client-side only
    const { SingularConfig, singularSdk } = await import('singular-sdk');
    const config = new SingularConfig(SINGULAR_API_KEY, SINGULAR_API_TOKEN, productId);
    const singularDeviceIdCookieDomain = typeof window === 'undefined'
        ? undefined
        : getSingularDeviceIdCookieDomain(window.location.hostname);
    if (singularDeviceIdCookieDomain) {
        config.withAutoPersistentSingularDeviceId(singularDeviceIdCookieDomain);
    }
    if (userId) {
        config.withCustomUserId(userId);
    }
    singularSdk.init(config);
    const eventName = 'initialized singular';
    const eventId = await getEventId(visitorId, eventName);
    singularSdk.event(eventName, { visitorId, eventId });
    return { SingularConfig, singularSdk };
};
export const useSingularAttribution = () => {
    const ctx = useContext(SingularAttributionContext);
    if (!ctx)
        throw new Error('useSingularAttribution must be used within SingularAttributionContext');
    return ctx;
};
//# sourceMappingURL=SingularAttributionProviderShared.js.map